import { useState, type MouseEvent, type ReactNode } from "react";
import { useAuth } from "@clerk/react";
import {
  ArrowLeft,
  CalendarDays,
  ExternalLink,
  Pencil,
  Share2,
  Trash2,
  UserRound,
} from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, Button } from "@ndd/ui";
import { Badge } from "@/components/badge";
import { authenticatedApi, ApiRequestError } from "@/lib/api";
import {
  meetingNoteDisplayTitle,
  type MeetingNoteBodyJson,
  type MeetingNoteData,
  type MeetingNoteShareRecipientOption,
} from "./meeting-note-types";
import { formatLocalDate, formatTimestamp } from "../../../../shared/local-date";
import { cn } from "@/lib/utils";
import { MeetingNoteShareDialog } from "./meeting-note-share-dialog";
import { AskAboutSourceAction } from "./ask-about-source-action";

interface MeetingNoteDetailProps {
  rdId: string;
  rdSlug: string;
  rdName: string;
  note: MeetingNoteData;
  recipientOptions: MeetingNoteShareRecipientOption[];
  onBack: () => void;
  onEdit: (note: MeetingNoteData) => void;
  onShared: () => void;
  onDeleted: () => void;
}

function shareStatusLabel(status: MeetingNoteData["shareHistory"][number]["status"]): string {
  switch (status) {
    case "sent":
      return "Sent";
    case "failed":
      return "Failed";
    case "attempted":
      return "Attempted";
  }
}

function safeHref(value: unknown): string | null {
  if (typeof value !== "string") return null;
  try {
    const url = new URL(value);
    return ["http:", "https:", "mailto:"].includes(url.protocol)
      ? url.toString()
      : null;
  } catch {
    return null;
  }
}

function renderTextMarks(
  text: string,
  marks: MeetingNoteBodyJson["marks"],
): ReactNode {
  return (marks ?? []).reduce<ReactNode>((node, mark, index) => {
    switch (mark.type) {
      case "bold":
        return <strong key={index}>{node}</strong>;
      case "italic":
        return <em key={index}>{node}</em>;
      case "strike":
        return <s key={index}>{node}</s>;
      case "code":
        return (
          <code key={index} className="rounded bg-sand-100 px-1 py-0.5 text-[0.9em]">
            {node}
          </code>
        );
      case "link": {
        const href = safeHref(mark.attrs?.href);
        if (!href) return node;
        return (
          <a
            key={index}
            href={href}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-interactive underline-offset-4 hover:underline"
          >
            {node}
          </a>
        );
      }
      default:
        return node;
    }
  }, text);
}

function renderInlineContent(node: MeetingNoteBodyJson, key: number): ReactNode {
  if (node.type === "text") {
    return renderTextMarks(node.text ?? "", node.marks);
  }
  if (node.type === "hardBreak") return <br key={key} />;
  return renderNodes(node.content);
}

function headingLevel(node: MeetingNoteBodyJson): 1 | 2 | 3 {
  const level = node.attrs?.level;
  return level === 1 || level === 2 || level === 3 ? level : 3;
}

function renderNodes(nodes: MeetingNoteBodyJson["content"]): ReactNode {
  return (nodes ?? []).map((node, index) => {
    const children =
      node.type === "text"
        ? renderInlineContent(node, index)
        : renderNodes(node.content);

    switch (node.type) {
      case "heading": {
        const level = headingLevel(node);
        const Heading = `h${level}` as "h1" | "h2" | "h3";
        return (
          <Heading
            key={index}
            className={cn(
              "mt-5 font-sans font-semibold text-foreground first:mt-0",
              level === 1 && "text-lg",
              level === 2 && "text-base",
              level === 3 && "text-[15px]",
            )}
          >
            {children}
          </Heading>
        );
      }
      case "paragraph":
        return (
          <p key={index} className="min-h-[1.45em] text-[14px] leading-6 text-sand-600">
            {children}
          </p>
        );
      case "bulletList":
        return (
          <ul key={index} className="ml-5 list-disc space-y-1 text-[14px] leading-6 text-sand-600">
            {children}
          </ul>
        );
      case "orderedList":
        return (
          <ol key={index} className="ml-5 list-decimal space-y-1 text-[14px] leading-6 text-sand-600">
            {children}
          </ol>
        );
      case "listItem":
        return (
          <li key={index} className="pl-1">
            {children}
          </li>
        );
      case "blockquote":
        return (
          <blockquote key={index} className="border-l-2 border-sand-200 pl-4 text-[14px] leading-6 text-sand-500">
            {children}
          </blockquote>
        );
      case "horizontalRule":
        return <hr key={index} className="border-sand-200" />;
      case "hardBreak":
        return <br key={index} />;
      case "text":
        return <span key={index}>{children}</span>;
      default:
        return <div key={index}>{children}</div>;
    }
  });
}

export function MeetingNoteBody({ bodyJson }: { bodyJson: MeetingNoteBodyJson }) {
  return <div className="space-y-3">{renderNodes(bodyJson.content)}</div>;
}

function MetadataItem({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
}) {
  return (
    <div className="flex min-w-0 items-start gap-2 rounded-xl border border-sand-200 bg-sand-50 px-3 py-2">
      <span className="mt-0.5 shrink-0 text-sand-400">{icon}</span>
      <span className="min-w-0">
        <span className="block text-[11px] font-semibold uppercase tracking-wider text-sand-400">
          {label}
        </span>
        <span className="block truncate text-[13px] font-medium text-foreground">
          {value}
        </span>
      </span>
    </div>
  );
}

export function MeetingNoteDetail({
  rdId,
  rdSlug,
  rdName,
  note,
  recipientOptions,
  onBack,
  onEdit,
  onShared,
  onDeleted,
}: MeetingNoteDetailProps) {
  const { getToken } = useAuth();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const title = meetingNoteDisplayTitle(note);

  async function handleConfirmDelete(e: MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    if (isDeleting) return;
    setIsDeleting(true);
    setDeleteError(null);

    try {
      await authenticatedApi(getToken, `/api/rds/${rdId}/meeting-notes/${note.id}`, {
        method: "DELETE",
      });
      setDeleteOpen(false);
      onDeleted();
    } catch (deleteRequestError) {
      if (deleteRequestError instanceof ApiRequestError) {
        const body = deleteRequestError.body as { error?: string } | undefined;
        setDeleteError(body?.error ?? "Failed to delete meeting note.");
      } else {
        setDeleteError("Something went wrong. Please try again.");
      }
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <div className="flex flex-col gap-4 px-5 py-4">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex w-fit items-center gap-1 text-xs font-semibold uppercase tracking-wider text-sand-500 transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to Meeting Notes
      </button>

      <div className="rounded-2xl border border-sand-200 bg-card shadow-[0_10px_24px_rgba(40,3,15,0.08)]">
        <div className="flex flex-col gap-4 border-b border-sand-200 px-5 py-4 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              {note.lastSharedAt && (
                <Badge variant="category" className="gap-1 px-2 py-0.5 text-[11px]">
                  <Share2 className="h-3 w-3" />
                  Shared
                </Badge>
              )}
              {note.fathomUrl && (
                <a
                  href={note.fathomUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-full border border-sand-200 bg-sand-100 px-2 py-0.5 text-[11px] font-semibold text-sand-500 transition-colors hover:text-foreground"
                >
                  <ExternalLink className="h-3 w-3" />
                  Fathom
                </a>
              )}
            </div>
            <h3 className="font-sans text-[20px] font-semibold leading-tight text-foreground">
              {title}
            </h3>
            <p className="mt-1 text-sm text-sand-500">
              RD check-in note for {rdName}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <AskAboutSourceAction rdSlug={rdSlug} source={{ type: "meeting_note", id: note.id }} />
            <MeetingNoteShareDialog
              rdId={rdId}
              note={note}
              recipientOptions={recipientOptions}
              onShared={onShared}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onEdit(note)}
              className="rounded-[10px] border-sand-200 text-sand-600 hover:text-foreground"
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setDeleteOpen(true)}
              className="rounded-[10px] border-error px-3 text-error hover:bg-error-light hover:text-error"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </Button>
          </div>
        </div>

        <div className="grid gap-2 border-b border-sand-200 bg-sand-50/50 px-5 py-4 md:grid-cols-4">
          <MetadataItem
            icon={<CalendarDays className="h-3.5 w-3.5" />}
            label="Meeting Date"
            value={formatLocalDate(note.meetingDate, "longWithYear")}
          />
          <MetadataItem
            icon={<UserRound className="h-3.5 w-3.5" />}
            label="RD"
            value={rdName}
          />
          <MetadataItem
            icon={<UserRound className="h-3.5 w-3.5" />}
            label="Supervisor"
            value={note.supervisorName || "Unassigned"}
          />
          <MetadataItem
            icon={<UserRound className="h-3.5 w-3.5" />}
            label="Author"
            value={note.authorName || "Unknown"}
          />
        </div>

        <div className="px-5 py-5">
          <MeetingNoteBody bodyJson={note.bodyJson} />
        </div>

        <div className="border-t border-sand-200 bg-sand-50/70 px-5 py-4">
          <div className="rounded-xl border border-dashed border-sand-200 bg-card px-4 py-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="text-[12px] font-semibold uppercase tracking-wider text-sand-500">
                  Share history
                </div>
                <div className="mt-1 text-sm text-sand-500">
                  {note.lastSharedAt
                    ? `Last shared ${formatTimestamp(note.lastSharedAt, "shortWithYearAndTime")}`
                    : "This note has not been shared yet."}
                </div>
              </div>
              <Badge
                variant="outline"
                className={cn(
                  "w-fit border-sand-200 text-sand-500",
                  !note.lastSharedAt && "border-dashed",
                )}
              >
                Share attempts are recorded for audit
              </Badge>
            </div>
            {note.shareHistory.length > 0 && (
              <div className="mt-3 divide-y divide-sand-100 border-t border-sand-100 pt-2">
                {note.shareHistory.map((share) => (
                  <div
                    key={share.id}
                    className="flex flex-col gap-1 py-2 text-sm sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0 text-sand-600">
                      <span className="font-medium text-foreground">
                        {share.senderName}
                      </span>{" "}
                      shared with {share.recipientName}
                    </div>
                    <div className="flex shrink-0 items-center gap-2 text-xs text-sand-500">
                      <Badge variant="outline" className="border-sand-200 text-sand-500">
                        {shareStatusLabel(share.status)}
                      </Badge>
                      <span>{formatTimestamp(share.sentAt ?? share.createdAt, "shortWithYearAndTime")}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this meeting note?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the note from the RD profile. The record is retained internally for audit history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError && <p className="text-sm text-error">{deleteError}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isDeleting}
              className="bg-error text-white hover:bg-error/90"
              onClick={handleConfirmDelete}
            >
              {isDeleting ? "Deleting..." : "Delete note"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
